//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { BodyText } from '@components/atoms/text/Text';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { Mixins } from '@theme/mixins';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { useGraphRightsCapabilityQuery } from '@uia/rights/hooks/useGraphRightsCapabilityQuery';
import { checkIsStatusPendingOrFinalized } from '@uia/rights/utils/statusUtils';

const ALLOWED_NETWORKS = [SocialNetwork.Instagram, SocialNetwork.Facebook];

export interface ChooseMessageCapabilitiesProps {
  cuid: Cuid;
  requestType: RightsRequestOptions;
}

export const ChooseMessageCapabilities = ({
  cuid,
  requestType,
}: ChooseMessageCapabilitiesProps) => {
  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: isCapable,
    isError: isErrorCapable,
    isLoading: isLoadingCapable,
  } = useGraphRightsCapabilityQuery(Cuid.toPartial(cuid));

  if (
    isLoadingMetadata ||
    isErrorMetadata ||
    isLoadingCapable ||
    isErrorCapable
  ) {
    return null;
  }

  const {
    rightsRequestStatus: { status },
    socialNetwork,
  } = metadata;

  const isPendingOrFinalized = checkIsStatusPendingOrFinalized(status);

  const isNetworkAllowed = ALLOWED_NETWORKS.includes(
    socialNetwork ?? SocialNetwork.Unknown,
  );

  if (!isNetworkAllowed || isPendingOrFinalized) {
    return null;
  }

  const isDmRequestType = requestType === RightsRequestOptions.DM;

  if (isCapable) {
    return isDmRequestType ? (
      <BodyText>
        Choose a message to request the rights to the content, then select{' '}
        <Bold>Copy and Paste in Direct Message</Bold>.
      </BodyText>
    ) : (
      <BodyText>
        You're in luck! The owner of this content <Bold>@mentioned</Bold> you in
        their post. To request rights, select the comment below and{' '}
        <Bold>we'll automatically post it</Bold> on their Instagram.
      </BodyText>
    );
  }

  return isDmRequestType ? (
    <BodyText>
      Choose a message to request the rights to the content, then select Copy
      and Paste to Instagram Direct Messenger.
    </BodyText>
  ) : (
    <BodyText>
      Choose a message to request the rights to the content, then select Copy
      and Paste to Instagram Comments.
    </BodyText>
  );
};

const Bold = styled.span`
  ${Mixins.Text.BodyBold}
`;
